import Vue from "vue";
import Router from "vue-router";
import courses from "./courses";
import education from "./education";
import store from "@/store";
import tokenUtils from "@/utils/tokens";

// auth
const Auth = () => import("@/modules/auth/index.vue");
const Registration = () => import("@/modules/auth/pages/registration/index.vue");
const SingIn = () => import("@/modules/auth/pages/sing-in/index.vue");
const RestorePassword = () => import("@/modules/auth/pages/restore-password/index.vue");
const SetNewPassword = () => import("@/modules/auth/pages/set-new-password/index.vue");

// system pages
const PageNotFound = () => import("@/modules/system/page-not-found/index.vue");
const ServerNotFound = () => import("@/modules/system/server-not-found/index.vue");
const NotPermission = () => import("@/modules/system/not-permission/index.vue");

// home
const Home = () => import("@/modules/home/index.vue");

Vue.use(Router);

export const isAuthenticated = (to, from, next) => {
    if (!tokenUtils.hasActualToken()) {
        next({
            path: `/${store.getters.currentLanguage.translationKey}/login`,
            query: { redirect: to.fullPath },
        });
    } else {
        next();
    }

    return;
};

const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    routes: [
        {
            path: "/:lang?/not-found", // 404
            name: "not-found",
            component: PageNotFound,
        },
        {
            path: "/:lang?/server-not-found", // 500
            name: "server-not-found",
            component: ServerNotFound,
        },
        {
            path: "/:lang?/not-permission", // 403
            name: "not-permission",
            component: NotPermission,
        },
        {
            path: "/:lang?/player",
            name: "player",
            component: () => import("@/modules/player/index.vue"),
        },
        {
            path: "/:lang?/",
            name: "auth",
            component: Auth,
            redirect: "/login",
            children: [
                {
                    path: "/:lang?/login",
                    name: "sing-in",
                    component: SingIn,
                },
                {
                    path: "/:lang?/registration",
                    name: "registration",
                    component: Registration,
                },
                {
                    path: "/:lang?/restore-password",
                    name: "restore-password",
                    component: RestorePassword,
                },
                {
                    path: "/:lang?/set-new-password",
                    name: "set-new-password",
                    component: SetNewPassword,
                },
            ],
        },
        {
            path: "/:lang(.{2})?/",
            name: "profile",
            component: Home,
            children: [
                ...courses(isAuthenticated),
                ...education(isAuthenticated),
                {
                    path: "/:lang?/events",
                    name: "events",
                    component: () => import("@/modules/events/pages/main/index.vue"),
                    meta: {
                        isChildrenRoute: false,
                        breadcrumbs: [
                            {
                                bcDynamic: false,
                                bcLabel: "main",
                                name: "main",
                            },
                            {
                                bcDynamic: false,
                                bcLabel: "events",
                                name: "events",
                            },
                        ],
                    },
                },
                {
                    path: "/:lang?/events/:slug",
                    name: "events-view",
                    component: () => import("@/modules/events/pages/detail/index.vue"),
                    meta: {
                        isChildrenRoute: true,
                        breadcrumbs: [
                            {
                                bcDynamic: false,
                                bcLabel: "main",
                                name: "main",
                            },
                            {
                                bcDynamic: false,
                                bcLabel: "events",
                                name: "events",
                            },
                            {
                                bcDynamic: true,
                                bcModule: "events",
                                bcGetter: "currentItem",
                                name: "events-view",
                            },
                        ],
                    },
                },
                {
                    path: "/:lang?/news",
                    name: "news",
                    component: () => import("@/modules/news/pages/main/index.vue"),
                    meta: {
                        isChildrenRoute: false,
                        breadcrumbs: [
                            {
                                bcDynamic: false,
                                bcLabel: "main",
                                name: "news",
                            },
                        ],
                    },
                },
                {
                    path: "/:lang?/news/:slug",
                    name: "news-detail",
                    component: () => import("@/modules/news/pages/detail/index.vue"),
                    meta: {
                        isChildrenRoute: true,
                        breadcrumbs: [
                            {
                                bcDynamic: false,
                                bcLabel: "main",
                                name: "news",
                            },
                            {
                                bcDynamic: true,
                                bcModule: "news",
                                bcGetter: "currentItem",
                                name: "news-view",
                            },
                        ],
                    },
                },
                {
                    path: "/:lang?/shop",
                    name: "shop",
                    component: () => import("@/modules/shop/index.vue"),
                    meta: {
                        isChildrenRoute: false,
                        breadcrumbs: [
                            {
                                bcDynamic: false,
                                bcLabel: "main",
                                name: "courses",
                            },
                            {
                                bcDynamic: false,
                                bcLabel: "shop",
                                name: "shop",
                            },
                        ],
                    },
                },
                {
                    path: "/:lang?/profile",
                    name: "profile-user",
                    component: () => import("@/modules/profile/index.vue"),
                    redirect: window.innerWidth > 900 ? "/:lang?/profile/main" : "",
                    beforeEnter: isAuthenticated,
                    children: [
                        {
                            path: "main",
                            name: "profile-main",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/main/index.vue"),
                        },
                        {
                            path: "contacts",
                            name: "profile-contacts",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/contacts/index.vue"),
                        },
                        {
                            path: "certificates",
                            name: "profile-certificates",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/certificates/index.vue"),
                        },
                        {
                            path: "homework",
                            name: "profile-homework",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/homework/index.vue"),
                        },
                        // {
                        //   path: 'notifications',
                        //   name: 'profile-notifications',
                        //   beforeEnter: isAuthenticated,
                        //   component: () => import('@/modules/profile/pages/notifications/index.vue'),
                        // },
                        {
                            path: "payment-methods",
                            name: "profile-payment-methods",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/payments/index.vue"),
                        },
                        {
                            path: "payment-methods/add",
                            name: "profile-payment-methods-add",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/payments-add/index.vue"),
                        },
                        {
                            path: "subscription",
                            name: "profile-subscription",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import(
                                    "@/modules/profile/pages/payments-history/index.vue"
                                ),
                        },
                        {
                            path: "settings",
                            name: "profile-settings",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import("@/modules/profile/pages/settings/index.vue"),
                        },
                        {
                            path: "change-password",
                            name: "profile-change-password",
                            beforeEnter: isAuthenticated,
                            component: () =>
                                import(
                                    "@/modules/profile/pages/change-password/index.vue"
                                ),
                        },
                    ],
                },
            ],
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        let el = document.querySelector(".app");
        if (to.path !== from.path) {
            if (savedPosition) {
                return savedPosition;
            } else {
                el.scrollTop = 0;

                return { x: 0, y: 0 };
            }
        }
    },
});

export default router;
